import { makeAutoObservable, observable, runInAction } from "mobx";
import {
  CreateSensorBatch,
  EditSensorBatch,
  SensorBatch,
  SBSensor,
  CheckSensorBatchFiles,
} from "../models/sensor-batch-models";
import {
  checkSensorBatchFiles,
  deleteSensorBatch,
  editSensorBatch,
  getAllSensorBatches,
  postNewSensorBatch,
} from "../services/sensor-batch-service";
import { StoreResults } from "../../../../stores/stores-helper";
import { convertDDMMMYYYYtoDate } from "components/utils/date-formatter-utils";

class SensorBatchStore {
  sensorBatchMap: Record<string, SensorBatch> = observable.object({});
  sensorsMap: Record<string, SBSensor> = observable.object({});
  sensorBatchLoading: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  getAllSensors():SBSensor[] {
    return Object.values(this.sensorsMap);
  }
  get sensorBatches() {
    return Object.values(this.sensorBatchMap);
  }

  getSensorByMacAddress = (macAddress: string): SBSensor | undefined => {
    for (const batch of this.sensorBatches) {
      const sensor = batch.sensors.find((s) => s.macAddress === macAddress);
      if (sensor) {
        return sensor;
      }
    }
    return undefined;
  };

  getSensorBatchBySensorMacAddress=(macAddress: string):SensorBatch | undefined =>{
    for (const batch of this.sensorBatches) {
      const sensor = batch.sensors.find((s) => s.macAddress === macAddress);
      if (sensor) {
        return batch;
      }
    }
    return undefined;
  };

  get getBatchSensorsByPartNumber(): (partNumber: string) => SBSensor[] {
    return (partNumber: string): SBSensor[] => {
      // Find all batches that match the given part number
      const matchingBatches = this.sensorBatches.filter(
        (sb) => sb.partNumber === partNumber
      );

      // Aggregate all sensors from the matching batches
      const sensors = matchingBatches.flatMap((sb) => sb.sensors || []);
      return sensors;
    };
}

  get allPartNumbers() {
    try {
      if (this.sensorBatchLoading) {
        return [];
      }

      if (!this.sensorBatches || this.sensorBatches.length === 0) return [];

      // Extract partNumber from each sensor batch and use a Set to ensure uniqueness.
      const hardwareVariants = new Set(
        this.sensorBatches
          .filter((batch) => batch.partNumber && batch.partNumber.trim() !== "")
          .map((batch) => batch.partNumber.trim())
      );

      // Convert the Set back to an array
      return Array.from(hardwareVariants);
    } catch (e) {
      console.log("Something went wrong while allPartNumbers:", e);
      return []
    }
  }

  loadAllSensorBatches = async () => {
    if (this.sensorBatchLoading) {
      return;
    }
    runInAction(() => {
      this.sensorBatchLoading = true;
    });

    try {
      const data = await getAllSensorBatches();
      runInAction(() => {
        for (let sensorBatch of data) {
          this.sensorBatchMap[sensorBatch.id] = observable.object(sensorBatch);
          sensorBatch.sensors.forEach((sensor) => {
            this.sensorsMap[sensor.macAddress] = sensor;
          });
        }
        this.sortSensorBatchMap();
      });
    } catch (e) {
      console.log(e);
    } finally {
      this.sensorBatchLoading = false;
    }
  };

  checkFilesBySensorBatchId = async (
    id: string
  ): Promise<CheckSensorBatchFiles> => {
    const result = await checkSensorBatchFiles(id); // Await only once

    runInAction(() => {
      this.sensorBatchMap[id].isExistCsv = result.isExistCsv;
      this.sensorBatchMap[id].isExistPdf = result.isExistPdf;
    });

    return result;
  };

  private sortSensorBatchMap() {
    const sortedKeys = Object.keys(this.sensorBatchMap).sort((a, b) => {
      const dateA = new Date(this.sensorBatchMap[a].batchDate);
      const dateB = new Date(this.sensorBatchMap[b].batchDate);
      return dateB.getTime() - dateA.getTime(); // Descending order
    });

    const sortedSensorBatchMap: Record<string, SensorBatch> = {};
    for (const key of sortedKeys) {
      sortedSensorBatchMap[key] = this.sensorBatchMap[key];
    }

    this.sensorBatchMap = sortedSensorBatchMap;
  }

  // Update sensors in a specific batch
  updateSensorsInBatch = (sensorBatchId: string, sensors: SBSensor[]) => {
    runInAction(() => {
    const batch = this.sensorBatchMap[sensorBatchId];
    if (batch) {
      batch.sensors = observable.array(sensors);
      this.sortSensorBatchMap();
    }
  });
  };

  createEntity = async (
    entity: Partial<CreateSensorBatch>
  ): Promise<StoreResults> => {
    if (this.sensorBatchLoading) {
      return { passed: false, message: "", entityId: "" };
    }

    runInAction(() => {
      this.sensorBatchLoading = true;
    });

    try {
      const response = await postNewSensorBatch(entity as Partial<any>);
      if (response.statusCode === 200) {
        const createdEntity = response.result;
        const entityId = createdEntity?.id ?? "";
        runInAction(() => {
          this.sensorBatchMap[entityId] = observable.object(
            createdEntity as SensorBatch
          );
          //resort Sensor Batches
          this.sortSensorBatchMap();
        });
        return { passed: true, message: "", entityId };
      } else if (response.statusCode === 409) {
        return {
          passed: false,
          message: "Conflict-409: an entity with the same name is exist.",
          entityId: "",
        };
      } else {
        const message = "An error has occurred, please check the logs";
        return { passed: false, message, entityId: "" };
      }
    } catch (error) {
      console.error("Error creating entity:", error);
      return {
        passed: false,
        message: "An error has occurred, please check the logs",
        entityId: "",
      };
    } finally {
      runInAction(() => {
        this.sensorBatchLoading = false;
      });
    }
  };

  editEntity = async (
    entity: Partial<EditSensorBatch>
  ): Promise<StoreResults> => {
    if (this.sensorBatchLoading) {
      return { passed: false, message: "", entityId: "" };
    }
    runInAction(() => {
      this.sensorBatchLoading = true;
    });
    try {
      const response = await editSensorBatch(entity as Partial<any>);

      if (response.statusCode === 200) {
        let entityId = "";
        runInAction(() => {
          const enitiySensorBatch = entity as EditSensorBatch;
          entityId = enitiySensorBatch?.id as string;
          const existingBatch = this.sensorBatchMap[entityId];

          if (existingBatch) {
            const updatedSensorBatch: SensorBatch = {
              ...existingBatch,
              batchDate:
                convertDDMMMYYYYtoDate(enitiySensorBatch?.batchDate) ??
                new Date(),
              purchaseOrder: enitiySensorBatch?.purchaseOrder as string,
              manufacturer: enitiySensorBatch?.manufacturer as string,
              description: enitiySensorBatch?.description as string,
              partNumber: enitiySensorBatch?.partNumber as string,
              isVirtualSensorBatch: enitiySensorBatch?.isVirtualSensorBatch,
            };
            // Check for CSV
            if (
              enitiySensorBatch?.sensorBatchCsv &&
              enitiySensorBatch.sensorBatchCsv.size > 0
            ) {
              updatedSensorBatch.isExistCsv = true;
            }

            // Check for PDF
            if (
              enitiySensorBatch?.sensorBatchPdf &&
              enitiySensorBatch.sensorBatchPdf.size > 0
            ) {
              updatedSensorBatch.isExistPdf = true;
            }
            // Update in the map
            this.sensorBatchMap[entityId] = updatedSensorBatch;

            //resort Sensor Batches
            this.sortSensorBatchMap();
          }
        });
        return { passed: true, message: "", entityId };
      } else {
        const message = "An error has occurred, please check the logs";
        return { passed: false, message, entityId: "" };
      }
    } catch (error) {
      console.error("Error editing entity:", error);
      return {
        passed: false,
        message: "An error has occurred, please check the logs",
        entityId: "",
      };
    } finally {
      runInAction(() => {
        this.sensorBatchLoading = false;
      });
    }
  };

  deleteEntitiy = async (entityId: string): Promise<StoreResults> => {
    if (this.sensorBatchLoading) {
      return { passed: false, message: "" };
    }

    runInAction(() => {
      this.sensorBatchLoading = true;
    });
    try {
      const response = await deleteSensorBatch(entityId as string);
      if (response.statusCode === 200) {
        runInAction(() => {
          delete this.sensorBatchMap[entityId];
          return { passed: true, message: "" };
        });
        return { passed: true, message: "" };
      } else {
        const message = "An error has occurred, please check the logs";
        return { passed: false, message };
      }
    } catch (error) {
      console.error("Error creating customer:", error);
      return {
        passed: false,
        message: "An error has occurred, please check the logs",
      };
    } finally {
      runInAction(() => {
        this.sensorBatchLoading = false;
      });
    }
  };
}
export default new SensorBatchStore();
